import type { StoreLimitUpgradeTarget, StoreTrialEligibilityState } from '@common/api/authService';
import type { Translate } from '@client-app/i18n';
import {
    getPricingTierIntegrationConnections,
    getPricingTierLimits,
    getPricingTiers,
} from '@common/config/pricingCatalog';
import {
    isPluginPlanCode,
    PLUGIN_PLAN_LIMITS,
    PLUGIN_PLAN_NAME,
} from '@/utils/pluginWorkspace';
import {
    resolveOnboardingConnectionInstances,
    resolvePluginConnectionAdditionState,
    type OnboardingServerIntegrationLike,
    type PluginConnectionLimitReason,
} from '@/components/Onboarding/onboardingConnectionInstances';
import { getIntegrationProviderForTool } from './integrationCatalog';

export interface ConnectRestrictionPayload {
    restrictionState?: StoreTrialEligibilityState | null;
    eligibilityState?: StoreTrialEligibilityState | null;
    reasonCode?: string | null;
    blockConnect?: boolean | null;
    upgradeRequired?: boolean | null;
    ctaAction?: string | null;
    message?: string | null;
    eligibilityMessage?: string | null;
    allowsFreshTrial?: boolean | null;
    trialRemainingDays?: number | null;
    currentWorkspaceId?: string | null;
    currentWorkspaceName?: string | null;
    currentStoreCount?: number | null;
    storeLimit?: number | null;
    upgradeTarget?: StoreLimitUpgradeTarget | null;
}

export interface NormalizedConnectRestriction {
    restrictionState: StoreTrialEligibilityState | null;
    blockConnect: boolean;
    upgradeRequired: boolean;
    ctaAction: string | null;
    message: string | null;
    allowsFreshTrial: boolean | null;
    trialRemainingDays: number | null;
    currentWorkspaceId: string | null;
    currentWorkspaceName: string | null;
    currentStoreCount: number | null;
    storeLimit: number | null;
    upgradeTarget: StoreLimitUpgradeTarget | null;
}

const CONNECT_RESTRICTION_STATES: readonly StoreTrialEligibilityState[] = [
    'eligible',
    'trial_resume_allowed',
    'already_bound',
    'trial_valid',
    'trial_expired',
    'plan_expired',
    'payment_past_due',
    'store_limit_reached',
    'integration_group_limit_reached',
    'total_connection_limit_reached',
];

const CONNECT_RESTRICTION_STATE_SET = new Set<string>(CONNECT_RESTRICTION_STATES);

export const normalizeConnectRestrictionState = (
    value?: string | null,
): StoreTrialEligibilityState | null => {
    const normalized = String(value || '').trim().toLowerCase();
    return CONNECT_RESTRICTION_STATE_SET.has(normalized)
        ? normalized as StoreTrialEligibilityState
        : null;
};

export const normalizeConnectRestriction = (
    payload?: ConnectRestrictionPayload | null
): NormalizedConnectRestriction => {
    const reasonState = normalizeConnectRestrictionState(payload?.reasonCode ?? null);
    const restrictionState = normalizeConnectRestrictionState(
        payload?.restrictionState
        ?? payload?.eligibilityState
        ?? reasonState
        ?? null,
    );
    const allowsFreshTrial = payload?.allowsFreshTrial ?? null;
    const shouldInferBlockingRestriction = restrictionState !== null
        && restrictionState !== 'eligible'
        && restrictionState !== 'trial_valid'
        && restrictionState !== 'trial_resume_allowed';
    let inferredCtaAction: string | null = null;
    if (reasonState === 'trial_expired' || reasonState === 'plan_expired') {
        inferredCtaAction = 'renew_subscription';
    } else if (reasonState === 'payment_past_due') {
        inferredCtaAction = 'update_payment_method';
    }

    return {
        restrictionState,
        blockConnect: payload?.blockConnect ?? shouldInferBlockingRestriction,
        upgradeRequired: payload?.upgradeRequired ?? shouldInferBlockingRestriction,
        ctaAction: payload?.ctaAction ?? inferredCtaAction,
        message: payload?.message ?? payload?.eligibilityMessage ?? null,
        allowsFreshTrial,
        trialRemainingDays: payload?.trialRemainingDays ?? null,
        currentWorkspaceId: payload?.currentWorkspaceId ?? null,
        currentWorkspaceName: payload?.currentWorkspaceName ?? null,
        currentStoreCount: payload?.currentStoreCount ?? null,
        storeLimit: payload?.storeLimit ?? null,
        upgradeTarget: payload?.upgradeTarget ?? null,
    };
};

export interface StoreLimitPrompt {
    title: string;
    description: string;
    primaryActionLabel: string;
    secondaryActionLabel: string;
    upgradeTarget: StoreLimitUpgradeTarget | null;
    primaryActionTarget?: 'plans' | 'sales' | 'none';
}

export interface SubscriptionRestrictionPrompt {
    title: string;
    description: string;
    detail: string | null;
    primaryActionLabel: string;
    secondaryActionLabel: string | null;
    primaryActionTarget: 'plans' | 'none';
}

export interface ConnectRestrictionCopyContext {
    planName: 'Solo' | 'Team' | 'Plugin' | null;
    storeLimit: number | null;
    otherIntegrationLimit: number | null;
    totalIntegrationLimit: number | null;
    isPluginPlan?: boolean;
    isHighestStandardTier?: boolean;
    usesAdminPooledConnectionCopy?: boolean;
}

const EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT: ConnectRestrictionCopyContext = {
    planName: null,
    storeLimit: null,
    otherIntegrationLimit: null,
    totalIntegrationLimit: null,
};

export const resolveConnectionLimitUpgradeTarget = (
    upgradeTarget: StoreLimitUpgradeTarget | null,
    copyContext: ConnectRestrictionCopyContext,
): StoreLimitUpgradeTarget | null => {
    if (upgradeTarget !== 'SALES') {
        return upgradeTarget;
    }

    // The shared catalog is authoritative for known standard plans. A stale
    // SALES response must not skip available self-serve upgrades.
    return copyContext.isHighestStandardTier === false ? null : 'SALES';
};

export const resolveConnectRestrictionCopyContext = (
    planCode?: string | null,
): ConnectRestrictionCopyContext => {
    const normalizedPlanCode = String(planCode || '').trim().toUpperCase();
    if (isPluginPlanCode(normalizedPlanCode)) {
        const pluginPlanName = normalizedPlanCode.startsWith('PLUGIN_TEAM_')
            ? 'Team'
            : normalizedPlanCode === 'PLUGIN_FREE'
                || normalizedPlanCode === 'PLUGIN_PLAN'
                || normalizedPlanCode.startsWith('PLUGIN_SOLO_')
                ? 'Solo'
                : PLUGIN_PLAN_NAME;
        return {
            planName: pluginPlanName,
            storeLimit: PLUGIN_PLAN_LIMITS.storeConnections,
            otherIntegrationLimit: PLUGIN_PLAN_LIMITS.otherIntegrationConnections,
            totalIntegrationLimit: PLUGIN_PLAN_LIMITS.totalIntegrationConnections,
            isPluginPlan: true,
        };
    }

    const tier = [...getPricingTiers('solo'), ...getPricingTiers('team')]
        .find((candidate) => candidate.planCode === normalizedPlanCode);

    if (!tier) {
        return EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT;
    }

    const limits = getPricingTierLimits(tier, 'monthly');
    const integrationLimits = getPricingTierIntegrationConnections(tier);
    const teamTiers = getPricingTiers('team');
    const highestStandardTier = teamTiers[teamTiers.length - 1];

    return {
        planName: tier.group === 'solo' ? 'Solo' : 'Team',
        storeLimit: typeof limits.storeConnections === 'number' ? limits.storeConnections : null,
        otherIntegrationLimit: typeof integrationLimits.otherIntegrations === 'number'
            ? integrationLimits.otherIntegrations
            : null,
        totalIntegrationLimit: typeof integrationLimits.total === 'number'
            ? integrationLimits.total
            : null,
        isHighestStandardTier: tier.planCode === highestStandardTier?.planCode,
    };
};

export interface ConnectRestrictionAction {
    shouldBlockConnect: boolean;
    shouldOpenPricing: boolean;
    shouldShowSubscriptionPrompt: boolean;
    shouldShowStoreLimitPrompt: boolean;
    message: string | null;
    restrictionState: StoreTrialEligibilityState | null;
    currentStoreCount: number | null;
    storeLimit: number | null;
    upgradeTarget: StoreLimitUpgradeTarget | null;
    storeLimitPrompt: StoreLimitPrompt | null;
    subscriptionPrompt: SubscriptionRestrictionPrompt | null;
}

export const shouldShowPluginFreeTrialUsedPrompt = ({
    isPluginAdmin,
    isTrialWorkspace,
    restrictionState,
}: {
    isPluginAdmin: boolean;
    isTrialWorkspace: boolean;
    restrictionState: StoreTrialEligibilityState | null | undefined;
}): boolean => (
    isPluginAdmin
    && isTrialWorkspace
    && (restrictionState === 'trial_expired' || restrictionState === 'trial_valid')
);

export type PluginAdminConnectPreflight =
    | {
        kind: 'allowed';
    }
    | {
        kind: 'tool_not_included';
        tool: string;
    }
    | {
        kind: 'connection_limit_reached';
        reason: PluginConnectionLimitReason;
        current: number;
        limit: number;
    };

const PLUGIN_TEAM_COLLABORATION_TOOL_SET = new Set(['DingTalk', 'Feishu', 'Slack']);
const SOLO_TEAM_COLLABORATION_TOOL_SET = new Set(['Feishu', 'DingTalk', 'Slack']);

const isPluginTeamPlan = (planCode?: string | null): boolean => (
    String(planCode || '').trim().toUpperCase().startsWith('PLUGIN_TEAM_')
);
export const isSoloTeamCollaborationConnectionRestricted = ({
    isSoloWorkspace,
    tool,
}: {
    isSoloWorkspace: boolean;
    tool: string;
}): boolean => isSoloWorkspace && SOLO_TEAM_COLLABORATION_TOOL_SET.has(tool);

export const resolvePluginAdminConnectPreflight = ({
    enabled,
    tool,
    integrations,
    planCode,
}: {
    enabled: boolean;
    tool: string;
    integrations: readonly OnboardingServerIntegrationLike[];
    planCode?: string | null;
}): PluginAdminConnectPreflight => {
    if (!enabled) {
        return { kind: 'allowed' };
    }

    if (PLUGIN_TEAM_COLLABORATION_TOOL_SET.has(tool) && !isPluginTeamPlan(planCode)) {
        return {
            kind: 'tool_not_included',
            tool,
        };
    }

    const additionState = resolvePluginConnectionAdditionState({
        instances: resolveOnboardingConnectionInstances(integrations),
        provider: getIntegrationProviderForTool(tool) || tool,
    });

    if (!additionState.allowed && additionState.reason) {
        return {
            kind: 'connection_limit_reached',
            reason: additionState.reason,
            current: additionState.current,
            limit: additionState.limit,
        };
    }

    return { kind: 'allowed' };
};

const SUBSCRIPTION_PROMPT_RESTRICTION_STATES: StoreTrialEligibilityState[] = [
    'already_bound',
    'plan_expired',
    'payment_past_due',
    'trial_expired',
    'trial_valid',
];

const LIMIT_PROMPT_RESTRICTION_STATES: StoreTrialEligibilityState[] = [
    'store_limit_reached',
    'integration_group_limit_reached',
    'total_connection_limit_reached',
];

const buildStoreLimitPrompt = (
    normalized: NormalizedConnectRestriction,
    t: Translate,
    copyContext: ConnectRestrictionCopyContext,
): StoreLimitPrompt => {
    if (copyContext.isPluginPlan) {
        const storeLimit = normalized.storeLimit ?? copyContext.storeLimit;
        const storeLimitLabel = storeLimit
            ? t('website.integrationsSettings.connectedStoreCount', {
                count: storeLimit,
                plural: storeLimit === 1 ? '' : 's',
            })
            : null;
        const pluginPromptBase = {
            primaryActionLabel: t('website.integrationsSettings.pluginRestrictionAcknowledge'),
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: normalized.upgradeTarget,
            primaryActionTarget: 'none' as const,
        };

        if (normalized.restrictionState === 'integration_group_limit_reached') {
            return {
                title: t('website.integrationsSettings.otherIntegrationConnectionLimitReached'),
                description: t('website.integrationsSettings.pluginOtherIntegrationLimitDescription', {
                    limit: copyContext.otherIntegrationLimit,
                }),
                ...pluginPromptBase,
            };
        }

        if (normalized.restrictionState === 'total_connection_limit_reached') {
            return {
                title: t('website.integrationsSettings.totalIntegrationConnectionLimitReached'),
                description: t('website.integrationsSettings.pluginTotalIntegrationLimitDescription', {
                    limit: copyContext.totalIntegrationLimit,
                }),
                ...pluginPromptBase,
            };
        }

        return {
            title: t('website.integrationsSettings.storeConnectionLimitReached'),
            description: t('website.integrationsSettings.pluginStoreLimitDescription', {
                limit: storeLimitLabel,
            }),
            ...pluginPromptBase,
        };
    }

    const effectiveUpgradeTarget = resolveConnectionLimitUpgradeTarget(
        normalized.upgradeTarget,
        copyContext,
    );
    const isSalesTarget = effectiveUpgradeTarget === 'SALES';
    const primaryActionLabel = isSalesTarget
        ? t('website.pricing.contactSales')
        : t('website.pricing.viewPlans');

    if (copyContext.usesAdminPooledConnectionCopy) {
        const hasKnownPooledLimits = Boolean(
            copyContext.planName
            && copyContext.storeLimit
            && copyContext.totalIntegrationLimit,
        );
        const promptBase = {
            primaryActionLabel,
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: effectiveUpgradeTarget,
            primaryActionTarget: isSalesTarget ? 'sales' as const : 'plans' as const,
        };

        if (normalized.restrictionState === 'store_limit_reached') {
            return {
                title: t('website.integrationsSettings.storeConnectionLimitReached'),
                description: hasKnownPooledLimits
                    ? t(isSalesTarget
                        ? 'website.integrationsSettings.adminStoreLimitSalesDescription'
                        : 'website.integrationsSettings.adminStoreLimitPlansDescription', {
                        plan: copyContext.planName,
                        storeLimit: copyContext.storeLimit,
                        totalLimit: copyContext.totalIntegrationLimit,
                    })
                    : t(isSalesTarget
                        ? 'website.integrationsSettings.storeLimitSalesFallbackDescription'
                        : 'website.integrationsSettings.storeLimitPlansFallbackDescription'),
                ...promptBase,
            };
        }

        return {
            title: t('website.integrationsSettings.adminConnectionLimitReached'),
            description: hasKnownPooledLimits
                ? t(isSalesTarget
                    ? 'website.integrationsSettings.adminConnectionLimitSalesDescription'
                    : 'website.integrationsSettings.adminConnectionLimitPlansDescription', {
                    plan: copyContext.planName,
                    storeLimit: copyContext.storeLimit,
                    totalLimit: copyContext.totalIntegrationLimit,
                })
                : t(isSalesTarget
                    ? 'website.integrationsSettings.totalIntegrationLimitSalesFallbackDescription'
                    : 'website.integrationsSettings.totalIntegrationLimitPlansFallbackDescription'),
            ...promptBase,
        };
    }

    if (normalized.restrictionState === 'integration_group_limit_reached') {
        const hasKnownLimit = Boolean(copyContext.planName && copyContext.otherIntegrationLimit);
        return {
            title: t('website.integrationsSettings.otherIntegrationConnectionLimitReached'),
            description: hasKnownLimit
                ? t(isSalesTarget
                    ? 'website.integrationsSettings.otherIntegrationLimitSalesDescription'
                    : 'website.integrationsSettings.otherIntegrationLimitPlansDescription', {
                    plan: copyContext.planName,
                    limit: copyContext.otherIntegrationLimit,
                })
                : t(isSalesTarget
                    ? 'website.integrationsSettings.otherIntegrationLimitSalesFallbackDescription'
                    : 'website.integrationsSettings.otherIntegrationLimitPlansFallbackDescription'),
            primaryActionLabel,
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: effectiveUpgradeTarget,
            primaryActionTarget: isSalesTarget ? 'sales' : 'plans',
        };
    }

    if (normalized.restrictionState === 'total_connection_limit_reached') {
        const hasKnownLimit = Boolean(copyContext.planName && copyContext.totalIntegrationLimit);
        return {
            title: t('website.integrationsSettings.totalIntegrationConnectionLimitReached'),
            description: hasKnownLimit
                ? t(isSalesTarget
                    ? 'website.integrationsSettings.totalIntegrationLimitSalesDescription'
                    : 'website.integrationsSettings.totalIntegrationLimitPlansDescription', {
                    plan: copyContext.planName,
                    limit: copyContext.totalIntegrationLimit,
                })
                : t(isSalesTarget
                    ? 'website.integrationsSettings.totalIntegrationLimitSalesFallbackDescription'
                    : 'website.integrationsSettings.totalIntegrationLimitPlansFallbackDescription'),
            primaryActionLabel,
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: effectiveUpgradeTarget,
            primaryActionTarget: isSalesTarget ? 'sales' : 'plans',
        };
    }

    const storeLimit = normalized.storeLimit ?? copyContext.storeLimit;
    const hasKnownLimit = Boolean(copyContext.planName && storeLimit);
    const storeLimitLabel = storeLimit
        ? t('website.integrationsSettings.connectedStoreCount', {
            count: storeLimit,
            plural: storeLimit === 1 ? '' : 's',
        })
        : null;

    return {
        title: t('website.integrationsSettings.storeConnectionLimitReached'),
        description: hasKnownLimit
            ? t(isSalesTarget
                ? 'website.integrationsSettings.storeLimitSalesDescription'
                : 'website.integrationsSettings.storeLimitPlansDescription', {
                plan: copyContext.planName,
                limit: storeLimitLabel,
            })
            : t(isSalesTarget
                ? 'website.integrationsSettings.storeLimitSalesFallbackDescription'
                : 'website.integrationsSettings.storeLimitPlansFallbackDescription'),
        primaryActionLabel,
        secondaryActionLabel: t('website.integrationsSettings.notNow'),
        upgradeTarget: effectiveUpgradeTarget,
        primaryActionTarget: isSalesTarget ? 'sales' : 'plans',
    };
};

const buildSubscriptionRestrictionPrompt = (
    normalized: NormalizedConnectRestriction,
    t: Translate,
    copyContext: ConnectRestrictionCopyContext,
): SubscriptionRestrictionPrompt => {
    const isPaymentMethodUpdateRestriction = normalized.ctaAction === 'update_payment_method'
        || normalized.restrictionState === 'payment_past_due';
    const isRenewSubscriptionRestriction = normalized.ctaAction === 'renew_subscription'
        || normalized.restrictionState === 'plan_expired';

    if (isPaymentMethodUpdateRestriction) {
        return {
            title: t('website.integrationsSettings.paymentPastDueForConnectTitle'),
            description: t('website.integrationsSettings.paymentPastDueForConnectDescription'),
            detail: null,
            primaryActionLabel: t('website.integrationsSettings.updatePaymentMethod'),
            secondaryActionLabel: null,
            primaryActionTarget: 'plans',
        };
    }

    if (isRenewSubscriptionRestriction) {
        const isTrialExpired = normalized.restrictionState === 'trial_expired';
        return {
            title: t(isTrialExpired
                ? 'website.integrationsSettings.freeTrialExpiredForConnectTitle'
                : 'website.integrationsSettings.subscriptionExpiredForConnectTitle'),
            description: t(isTrialExpired
                ? 'website.integrationsSettings.freeTrialExpiredForConnectDescription'
                : 'website.integrationsSettings.subscriptionExpiredForConnectDescription'),
            detail: null,
            primaryActionLabel: t(isTrialExpired
                ? 'website.workspaceSwitcher.startSubscription'
                : 'website.integrationsSettings.restoreSubscription'),
            secondaryActionLabel: null,
            primaryActionTarget: 'plans',
        };
    }

    if (copyContext.isPluginPlan) {
        return {
            title: t('website.integrationsSettings.currentTrialCantBeUsedHere'),
            description: t('website.integrationsSettings.currentTrialCantBeUsedHereDescription'),
            detail: normalized.message,
            primaryActionLabel: t('website.integrationsSettings.pluginRestrictionAcknowledge'),
            secondaryActionLabel: null,
            primaryActionTarget: 'none',
        };
    }

    return {
        title: t('website.integrationsSettings.currentTrialCantBeUsedHere'),
        description: t('website.integrationsSettings.currentTrialCantBeUsedHereDescription'),
        detail: normalized.message,
        primaryActionLabel: t('website.workspaceSwitcher.startSubscription'),
        secondaryActionLabel: t('website.integrationsSettings.tryAnotherApp'),
        primaryActionTarget: 'plans',
    };
};

export const getConnectRestrictionAction = (
    payload?: ConnectRestrictionPayload | null,
    t?: Translate,
    copyContext: ConnectRestrictionCopyContext = EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT,
): ConnectRestrictionAction => {
    const normalized = normalizeConnectRestriction(payload);
    const effectiveUpgradeTarget = resolveConnectionLimitUpgradeTarget(
        normalized.upgradeTarget,
        copyContext,
    );
    const shouldRouteToSales = effectiveUpgradeTarget === 'SALES';
    const shouldShowStoreLimitPrompt = normalized.blockConnect
        && normalized.restrictionState !== null
        && LIMIT_PROMPT_RESTRICTION_STATES.includes(normalized.restrictionState);
    const hasSubscriptionRestrictionState = normalized.restrictionState !== null
        && SUBSCRIPTION_PROMPT_RESTRICTION_STATES.includes(normalized.restrictionState);
    const shouldShowSubscriptionPrompt = !shouldShowStoreLimitPrompt && (
        normalized.blockConnect
            ? (normalized.upgradeRequired || hasSubscriptionRestrictionState)
            : (normalized.allowsFreshTrial === false && hasSubscriptionRestrictionState)
    );

    return {
        shouldBlockConnect: normalized.blockConnect,
        shouldOpenPricing: normalized.blockConnect
            && normalized.upgradeRequired
            && !shouldRouteToSales
            && !copyContext.isPluginPlan,
        shouldShowSubscriptionPrompt,
        shouldShowStoreLimitPrompt,
        message: normalized.message,
        restrictionState: normalized.restrictionState,
        currentStoreCount: normalized.currentStoreCount,
        storeLimit: normalized.storeLimit,
        upgradeTarget: effectiveUpgradeTarget,
        storeLimitPrompt: shouldShowStoreLimitPrompt && t
            ? buildStoreLimitPrompt(normalized, t, copyContext)
            : null,
        subscriptionPrompt: shouldShowSubscriptionPrompt && t
            ? buildSubscriptionRestrictionPrompt(normalized, t, copyContext)
            : null,
    };
};
